//@ts-check
import {useProductCombo} from '@/context/ProductCombo/ProductComboContext'
import {getProductList} from '@/services/ProductsService/product'
import {ProductComboSteps} from '@/types/productComboTypes'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {getDefaultQuantity} from '@/utils/productCombo/productComboValidation'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useDataTable} from '../useDataTable-hook'

/** @typedef {{column?: string, direction?: string}} SortConfig */
/** @typedef {{field: string, type: string, value?: string, values?: string[], operator?: string}} SearchFilter */
/** @typedef {{filters: SearchFilter[], sorting: SortConfig, cacheKey: (string | SearchFilter[])[]}} SelectAllMutationArgs */

/**
 * @param {import('@/types/productComboTypes').SegmentDetails[] | undefined | null} segments
 * @returns {string[] | null}
 */
const getSegmentIds = segments => {
  const segmentIds = segments?.flatMap(segment => (segment.segmentId ? [segment.segmentId] : []))

  return segmentIds?.length ? segmentIds : null
}

const SELECT_ALL_PAGE_SIZE = 9999
const PRODUCT_LIST_SELECT_ALL_QUERY_KEY = 'PRODUCT_LIST_SELECT_ALL'

/**
 * @param {SortConfig} sorting
 * @param {number} page
 * @param {number} pageSize
 * @returns {string}
 */
const buildQueryParams = ({column, direction}, page, pageSize) => {
  const params = new URLSearchParams()

  if (column) {
    params.append('sortBy', column)
    params.append('order', direction ?? 'DESC')
  } else {
    params.append('sortBy', 'productId')
    params.append('order', 'DESC')
  }

  params.append('page', String(page))
  params.append('pageSize', String(pageSize))

  return params.toString()
}

/**
 * @param {SearchFilter[]} filters
 * @param {SortConfig} sorting
 * @returns {(string | SearchFilter[])[]}
 */
const buildSelectAllCacheKey = (filters, sorting) => [
  PRODUCT_LIST_SELECT_ALL_QUERY_KEY,
  buildQueryParams(sorting, 0, SELECT_ALL_PAGE_SIZE),
  filters,
]

/**
 * @param {SearchFilter[]} filters
 * @param {SortConfig} sorting
 * @returns {Promise<any[]>}
 */
const fetchAllProducts = async (filters, sorting) => {
  const response = await getProductList(filters, buildQueryParams(sorting, 0, SELECT_ALL_PAGE_SIZE))
  return response?.data || []
}

export const useComboProductTable = (defaultPageSize = DEFAULT_TABLE_PAGE_SIZE) => {
  const queryClient = useQueryClient()
  const {
    setSortingObj,
    setSearchArry,
    pageIndex,
    currentPage,
    sortingObj,
    searchArr,
    onPageIndexChange,
    createSearchArray,
    resetAllBtnClicked,
    handleResetAll,
    isSelectAll,
    setIsSelectAll,
  } = useDataTable()

  const {productCombo, currentStep, updateProductCombo} = useProductCombo()
  const comboSorting = /** @type {SortConfig} */ (sortingObj)
  const includedSegments = productCombo?.segmentConfiguration?.includedSegments?.[0]?.segments
  const excludedSegments = productCombo?.segmentConfiguration?.excludedSegment?.segments
  const isReviewStep = currentStep === ProductComboSteps.REVIEW
  const isQueryEnabled = isReviewStep
    ? (productCombo.comboSkus?.length ?? 0) > 0
    : Boolean(productCombo?.routeTypeId && productCombo?.displayCategoryIds?.length)

  const queryParams = buildQueryParams(comboSorting, pageIndex, defaultPageSize)

  let segmentIds = null
  let operator = null

  if (includedSegments?.length) {
    segmentIds = getSegmentIds(includedSegments)
    operator = 'include'
  } else if (excludedSegments?.length) {
    segmentIds = getSegmentIds(excludedSegments)
    operator = 'exclude'
  }

  /** @type {SearchFilter[]} */
  const searchFilters = [
    ...searchArr,
    {
      field: 'isCombo',
      value: 'false',
      type: 'string',
    },
    {field: 'routeTypeId', value: productCombo.routeTypeId, type: 'string'},
    {
      field: 'displayCategoryId',
      values: productCombo.displayCategoryIds,
      type: 'string',
    },
    {field: 'status', value: 'active', type: 'string'},
  ]

  if (segmentIds && operator) {
    searchFilters.push({
      field: 'segmentId',
      values: segmentIds,
      type: 'string',
      operator,
    })
  }

  /** @type {SearchFilter[]} */
  const activeSearchFilters = isReviewStep
    ? [
        ...searchArr,
        {
          field: 'isCombo',
          value: 'false',
          type: 'string',
        },
        {
          field: 'productId',
          values: productCombo.comboSkus?.map(sku => sku.productId),
          type: 'string',
        },
      ]
    : searchFilters

  const onRowSelection = products => {
    updateProductCombo({
      comboSkus: products.map(product => ({
        ...product,
        quantity: getDefaultQuantity(product.multipleSellingUnits),
      })),
    })
  }

  const {mutateAsync: fetchAllProductsMutateAsync, isPending: isFetchingAllProducts} = useMutation(
    /** @type {import('@tanstack/react-query').UseMutationOptions<any[], Error, SelectAllMutationArgs>} */ ({
      mutationKey: [PRODUCT_LIST_SELECT_ALL_QUERY_KEY],
      mutationFn: async ({filters, sorting, cacheKey}) => {
        const cachedProducts = /** @type {any[] | undefined} */ (queryClient.getQueryData(cacheKey))

        if (cachedProducts) {
          return cachedProducts
        }

        const allProducts = await fetchAllProducts(filters, sorting)

        queryClient.setQueryData(cacheKey, allProducts)

        return allProducts
      },
    })
  )

  const handleIsSelectAll = async nextIsSelectAll => {
    setIsSelectAll(nextIsSelectAll)

    if (!nextIsSelectAll) {
      return
    }

    try {
      const allProducts = await fetchAllProductsMutateAsync({
        filters: activeSearchFilters,
        sorting: comboSorting,
        cacheKey: buildSelectAllCacheKey(activeSearchFilters, comboSorting),
      })

      onRowSelection(allProducts)
    } catch {
      setIsSelectAll(false)
    }
  }

  const {data, isLoading} = useQuery({
    queryKey: ['PRODUCT_LIST', queryParams, activeSearchFilters],
    queryFn: () => getProductList(activeSearchFilters, queryParams),
    enabled: isQueryEnabled,
  })

  return {
    setSortingObj,
    setSearchArry,
    pageIndex,
    currentPage,
    sortingObj,
    searchArr,
    onPageIndexChange,
    createSearchArray,
    resetAllBtnClicked,
    data,
    isLoading: isLoading || isFetchingAllProducts,
    handleResetAll,
    onRowSelection,
    isSelectAll,
    handleIsSelectAll,
  }
}
